/**
 * Instant page changes: the next page is fetched on hover/touch (so it is
 * usually already here when the click comes), the current content fades out
 * (220ms, cubic-in — Averify timings), main#MainContent is swapped for the new
 * page's main, and the new content fades in (380ms, cubic-out — CSS in
 * art-layout.css). Header, footer and the frame never move or reload.
 *
 * Only main is exchanged, so the header's scripts keep running. Section scripts
 * inside the new main are re-executed; shared assets already on the page are
 * not loaded twice (they delegate on document / observe mutations).
 *
 * Anything that isn't a plain same-origin page link — modified clicks, new
 * tabs, downloads, forms, cart / checkout / account, the theme editor, any
 * fetch or parse failure — falls back to a normal full navigation.
 */
(function () {
  if (window.Shopify && window.Shopify.designMode) return;

  var root = document.documentElement;
  var OUT_MS = 220;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var cache = new Map(); // href → { html, t } or { promise }
  var shownPath = location.pathname; // the path of the main that is on screen
  var TTL = 60 * 1000;
  var seq = 0;

  function clearOut() { root.classList.remove('art-tx-out'); }
  window.addEventListener('pageshow', function (e) { if (e.persisted) clearOut(); });

  // ---- eligibility -------------------------------------------------------
  function pageUrl(a) {
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return null;
    var href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return null;
    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return null; }
    if (url.origin !== location.origin) return null;
    if (/^\/(?:[a-z]{2}\/)?(cart|checkout|checkouts|account|admin|tools|apps|password)(\/|$)/.test(url.pathname)) return null;
    if (/\.(pdf|zip|jpe?g|png|webp|gif|svg|mp4|ics)$/i.test(url.pathname)) return null;
    return url;
  }

  // ---- prefetch ----------------------------------------------------------
  function fetchPage(url) {
    var key = url.href;
    var hit = cache.get(key);
    if (hit && hit.html && Date.now() - hit.t < TTL) return Promise.resolve(hit.html);
    if (hit && hit.promise) return hit.promise;
    var p = fetch(key, { credentials: 'same-origin', headers: { 'X-Art-Nav': '1' } })
      .then(function (r) {
        var ct = r.headers.get('content-type') || '';
        if (!r.ok || ct.indexOf('text/html') === -1 || r.redirected) throw new Error('not swappable');
        return r.text();
      })
      .then(function (html) { cache.set(key, { html: html, t: Date.now() }); return html; })
      .catch(function (err) { cache.delete(key); throw err; });
    cache.set(key, { promise: p });
    return p;
  }

  /* Warming the big picture behind a card.
     Prefetching the next page gets its HTML, not the photograph inside it —
     that is only discovered once the swap has happened, which is why a blog
     post or an event used to arrive and then fill in. A card can name the
     picture its page (or its modal) will want, and hovering or touching it
     starts that file early, at low priority so it never competes with the
     page the visitor is still reading. */
  var warmed = {};
  function warmImage(src) {
    if (!src || warmed[src]) return;
    warmed[src] = true;
    var im = new Image();
    im.decoding = 'async';
    if ('fetchPriority' in im) im.fetchPriority = 'low';
    im.src = src;
  }

  function prefetchFrom(e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (t) {
      var card = t.closest('[data-art-warm]');
      if (card) warmImage(card.getAttribute('data-art-warm'));
    }
    var a = t ? t.closest('a[href]') : null;
    var url = a && pageUrl(a);
    if (!url || url.href === location.href) return;
    fetchPage(url).catch(function () {});
  }
  document.addEventListener('mouseover', prefetchFrom, { passive: true });
  document.addEventListener('touchstart', prefetchFrom, { passive: true });
  document.addEventListener('focusin', prefetchFrom, { passive: true });

  // ---- swap --------------------------------------------------------------
  function samePath(a, b) {
    return a.replace(/\/$/, '') === b.replace(/\/$/, '');
  }

  function markCurrent(url) {
    var links = document.querySelectorAll('.art-header a[href], .art-header__mobile-nav a[href], .art-appnav__item[href]');
    links.forEach(function (a) {
      var u;
      try { u = new URL(a.href, location.href); } catch (err) { return; }
      var on = samePath(u.pathname, url.pathname);
      if (a.classList.contains('art-appnav__item')) a.classList.toggle('is-active', on);
      else if (on) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  function runScripts(container) {
    var loaded = {};
    document.querySelectorAll('script[src]').forEach(function (s) {
      if (!container.contains(s)) loaded[s.src.split('?')[0]] = true;
    });
    container.querySelectorAll('script').forEach(function (old) {
      if (old.type && old.type !== 'text/javascript' && old.type !== 'module') return; // JSON blobs stay as data
      if (old.src && loaded[old.src.split('?')[0]]) return; // shared asset already running (delegates / observes)
      var s = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) s.setAttribute(old.attributes[i].name, old.attributes[i].value);
      if (!old.src) s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  // New page starts at the top (or at its #hash) — instantly (Art.jumpTo).
  function jumpTo(target) { window.Art.jumpTo(target); }

  function swap(html, url, push) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('MainContent');
    var main = document.getElementById('MainContent');
    if (!fresh || !main) throw new Error('no main');
    document.title = doc.title;
    if (doc.body.className) document.body.className = doc.body.className;
    main.replaceWith(fresh); // inserted while html.art-tx-out → animation: none, opacity 0
    runScripts(fresh);
    markCurrent(url);
    shownPath = url.pathname;
    if (push) history.pushState({ artNav: true }, '', url.href);
    jumpTo(url.hash && document.querySelector(url.hash));
    // The fade-in belongs to in-place changes only; a first load paints at once.
    root.classList.add('art-tx-in');
    clearOut(); // rule flips back → fade-in keyframes start on the new main
    fresh.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('art:page:swap', { detail: { url: url.href } }));
  }

  // The mark, for a page that is taking its time. Held back 350ms: most
  // navigations land well inside that and a loader that blinks on every click
  // is worse than none.
  var pageLoader = document.querySelector('.art-loader--page');
  var loaderTimer = null;

  function showLoader() {
    if (!pageLoader) return;
    window.clearTimeout(loaderTimer);
    loaderTimer = window.setTimeout(function () { pageLoader.hidden = false; }, 350);
  }
  function hideLoader() {
    window.clearTimeout(loaderTimer);
    if (pageLoader) pageLoader.hidden = true;
  }

  // Add ?art-loader=1 to any URL to pin it on screen and look at it — it is
  // otherwise only on show for the half-second a slow page takes to arrive.
  if (pageLoader && /[?&]art-loader=1(&|$)/.test(window.location.search)) {
    pageLoader.hidden = false;
  }

  function navigate(url, push) {
    var my = ++seq;
    root.classList.add('art-tx-out');
    showLoader();
    var out = new Promise(function (res) { setTimeout(res, reduce ? 0 : OUT_MS); });
    Promise.all([out, fetchPage(url)])
      .then(function (r) { if (my !== seq) return; hideLoader(); swap(r[1], url, push); })
      .catch(function () { hideLoader(); if (my === seq) location.href = url.href; });
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    var url = a && pageUrl(a);
    if (!url) return;
    if (samePath(url.pathname, location.pathname) && url.search === location.search && url.hash) return; // in-page anchor
    e.preventDefault();
    navigate(url, true);
  }, false);

  // Other scripts can warm the cache (e.g. the events calendar on day hover).
  window.ArtNav = {
    prefetch: function (href) {
      try { var u = new URL(href, location.href); if (u.origin === location.origin) fetchPage(u).catch(function () {}); } catch (err) {}
    },
    warmImage: warmImage,
    fade: window.Art && window.Art.fade
  };

  history.replaceState({ artNav: true }, '', location.href);
  window.addEventListener('popstate', function (e) {
    var url = new URL(location.href);
    if (e.state && e.state.artNav) { navigate(url, false); return; }
    // Sections write their own history entries for a filter on the page they
    // belong to (?c= on the Gallery, a pill on the Shop) and handle those
    // themselves. But stepping back ONTO one from somewhere else is a real
    // page change, and ignoring it left the address bar on one page while a
    // different one stayed on screen — after which any tap lands somewhere
    // nobody asked for.
    if (!samePath(url.pathname, shownPath)) navigate(url, false);
  });
})();
